<template>
    <div class="px-3 py-3 pb-6 space-y-3 max-w-screen-2xl mx-auto">
      <!-- Project + Sprint selectors -->
      <div class="rounded-2xl border border-gray-200 bg-white p-3 dark:border-gray-800 dark:bg-gray-900">
        <div class="grid grid-cols-1 gap-2 sm:grid-cols-3">
          <v-autocomplete density="compact" variant="solo-filled" flat hide-details label="Project" :items="projects" item-title="title" item-value="id" v-model="selectedProject" @update:modelValue="onProjectChange"></v-autocomplete>
          <v-autocomplete density="compact" variant="solo-filled" flat hide-details label="Sprint" :items="sprintOptions" item-title="name" item-value="id" v-model="selectedSprint" :disabled="!selectedProject" @update:modelValue="getBoard"></v-autocomplete>
          <div class="flex items-center justify-end">
            <AddTicket
              v-if="!isAllSprints"
              :project-id="selectedProject"
              :sprint-id="selectedSprint"
              :sprint-start="selectedSprintObj ? selectedSprintObj.start_date : null"
              :sprint-end="selectedSprintObj ? selectedSprintObj.end_date : null"
              @toggle-message="EventMessage"
            ></AddTicket>
            <button v-else type="button" disabled title="Pick a specific sprint to add a ticket"
              class="inline-flex items-center gap-1.5 rounded-xl bg-amber-400 px-3.5 py-2 text-sm font-semibold text-gray-900 opacity-50">
              <v-icon size="18">mdi-plus</v-icon>Add Ticket
            </button>
          </div>
        </div>
      </div>

      <!-- Board header -->
      <div class="rounded-2xl border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-gray-900">
        <div class="flex flex-wrap items-center justify-between gap-3">
          <div class="min-w-0">
            <div class="flex items-center gap-1">
              <h2 class="text-sm font-bold text-gray-900 dark:text-gray-50">{{ boardTitle }}</h2>
              <button type="button" @click="wipDialog = true" title="WIP limits"
                class="rounded-lg p-1 text-gray-400 transition hover:bg-gray-100 dark:hover:bg-gray-800"><v-icon size="16">mdi-cog-outline</v-icon></button>
            </div>
            <div class="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-gray-500 dark:text-gray-400">
              <span class="inline-flex items-center gap-1"><v-icon size="14" color="info">mdi-arrow-down</v-icon> Low</span>
              <span class="inline-flex items-center gap-1"><v-icon size="14" color="warning">mdi-arrow-right</v-icon> Medium</span>
              <span class="inline-flex items-center gap-1"><v-icon size="14" color="error">mdi-arrow-up</v-icon> High</span>
              <span class="inline-flex items-center gap-1"><v-icon size="14" color="error">mdi-alert-circle-outline</v-icon> Overdue</span>
            </div>
          </div>
          <div class="w-full sm:w-60">
            <v-text-field density="compact" variant="solo-filled" flat hide-details placeholder="Search tickets…" prepend-inner-icon="mdi-magnify" v-model="search"></v-text-field>
          </div>
        </div>
      </div>

      <!-- Horizontal-scroll board: all columns stay visible on every screen. -->
      <div class="flex gap-3 overflow-x-auto pb-3">
        <div
          v-for="column in columns"
          :key="column.key"
          class="flex w-72 flex-none flex-col rounded-2xl border transition"
          :class="dragOverColumn === column.key
            ? 'border-amber-400 bg-amber-50 ring-2 ring-amber-400 dark:bg-amber-500/10'
            : 'border-gray-200 bg-gray-50 dark:border-gray-800 dark:bg-gray-900/40'"
          @dragover.prevent="dragOverColumn = column.key"
          @dragleave.self="dragOverColumn = null"
          @drop="onDrop(column.key)"
        >
          <div class="sticky top-0 z-[1] flex items-center justify-between rounded-t-2xl border-t-4 bg-gray-50 px-3 py-2.5 dark:bg-gray-900/80"
            :style="{ borderTopColor: columnColor(column.key) }">
            <div class="flex items-center gap-2">
              <span class="h-2.5 w-2.5 rounded-full" :style="{ background: columnColor(column.key) }"></span>
              <span class="text-sm font-bold text-gray-800 dark:text-gray-100">{{ column.label }}</span>
            </div>
            <span class="rounded-full px-2 py-0.5 text-[11px] font-bold"
              :class="isOverLimit(column.key) ? 'bg-rose-500 text-white' : 'bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-300'">
              {{ filteredTickets(column.key).length }}<span v-if="wipLimits[column.key] > 0"> / {{ wipLimits[column.key] }}</span>
            </span>
          </div>

          <div class="flex flex-col gap-2 overflow-y-auto p-2.5" style="min-height: 90px; max-height: calc(100vh - 320px)">
            <div v-if="filteredTickets(column.key).length === 0"
              class="flex flex-col items-center justify-center gap-1.5 rounded-lg border border-dashed border-gray-300 py-6 text-xs text-gray-400 dark:border-gray-700">
              <v-icon size="22">mdi-tray-arrow-down</v-icon>
              <span>Drop tickets here</span>
            </div>

            <div
              v-for="ticket in filteredTickets(column.key)"
              :key="ticket.id"
              draggable="true"
              class="cursor-grab rounded-xl border border-l-4 border-gray-200 bg-white p-3 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md active:cursor-grabbing dark:border-gray-800 dark:bg-gray-900"
              :style="{ borderLeftColor: priorityAccent(ticket.priority) }"
              @dragstart="onDragStart(ticket, column.key)"
              @dragend="dragOverColumn = null"
            >
              <div class="flex items-start justify-between gap-2">
                <span class="line-clamp-2 text-sm font-semibold leading-snug text-gray-900 dark:text-gray-50">{{ ticket.name }}</span>
                <ViewTicket :ticket-data="ticket" @deleted="getBoard" @transferred="getBoard" @updated="getBoard"></ViewTicket>
              </div>

              <div class="mt-1.5 flex flex-wrap gap-x-3 gap-y-1 text-xs text-gray-500 dark:text-gray-400">
                <span class="inline-flex items-center gap-1"><v-icon size="13">mdi-ticket-outline</v-icon>{{ ticket.ticket_id }}</span>
                <span class="inline-flex items-center gap-1"><v-icon size="13">mdi-calendar-blank-outline</v-icon>{{ formattedDate(ticket.created_at) }}</span>
              </div>

              <div class="mt-2.5 flex items-center justify-between gap-2 border-t border-gray-100 pt-2 dark:border-gray-800">
                <div class="flex items-center gap-2.5">
                  <PriorityChip :priority="ticket.priority" icon-only />
                  <span
                    v-if="ticket.due_date"
                    class="inline-flex items-center gap-1 text-xs font-semibold"
                    :class="isTicketOverdue(ticket) ? 'text-rose-600 dark:text-rose-400' : 'text-gray-500 dark:text-gray-400'"
                    :title="isTicketOverdue(ticket) ? 'Overdue' : 'Due date'"
                  >
                    <v-icon size="13">{{ isTicketOverdue(ticket) ? 'mdi-alert-circle-outline' : 'mdi-calendar-clock' }}</v-icon>
                    {{ formattedDate(ticket.due_date) }}
                  </span>
                </div>
                <div v-if="assigneeList(ticket).length" class="flex items-center">
                  <span
                    v-for="(a, i) in assigneeList(ticket).slice(0, 3)"
                    :key="a.id"
                    class="flex h-6 w-6 items-center justify-center rounded-full text-[10px] font-bold text-white ring-2 ring-white dark:ring-gray-900"
                    :style="{ background: avatarColor(a.fullname), marginLeft: i ? '-6px' : '0', zIndex: 3 - i }"
                    :title="a.fullname"
                  >{{ initials(a.fullname) }}</span>
                  <span v-if="assigneeList(ticket).length > 3" class="ml-1.5 text-xs text-gray-400">+{{ assigneeList(ticket).length - 3 }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

        <!-- WIP limit settings -->
        <v-dialog v-model="wipDialog" max-width="420">
            <v-card title="WIP limits" prepend-icon="mdi-cog">
                <v-card-text>
                    <p class="text-caption text-grey mb-2">Set a max number of tickets per column. 0 means no limit.</p>
                    <v-text-field
                        v-for="column in columns"
                        :key="column.key"
                        density="compact" variant="outlined" type="number" min="0"
                        :label="column.label"
                        v-model.number="wipLimits[column.key]"
                    ></v-text-field>
                </v-card-text>
                <v-divider></v-divider>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text="Close" variant="plain" @click="wipDialog = false"></v-btn>
                    <v-btn color="primary" text="Save" variant="flat" @click="saveWipLimits"></v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <Snackbar :toggle-snackbar="isshow" :response-message="message"></Snackbar>
    </div>
  </template>

  <script>
  import AddTicket from './AddTicket.vue';
  import Snackbar from './Snackbar.vue';
  import axios from 'axios';
  import utils from '@/utils';
  import constant from '@/constant';
  import ViewTicket from './ViewTicket.vue';
  import apiMixin from '@/mixins/apiMixin';
  import formatMixin from '@/mixins/formatMixin';
  import PriorityChip from '@/components/ui/PriorityChip.vue';

  export default {
    name: 'TaskPage',
    mixins: [apiMixin, formatMixin],
    components: {
        AddTicket,
        Snackbar,
        ViewTicket,
        PriorityChip
    },
    data() {
      return {
        constant: constant,
        isshow: false,
        message: "",
        search: "",
        projects: [],
        sprints: [],
        selectedProject: null,
        selectedSprint: null,
        draggedTicket: null,
        draggedFrom: null,
        dragOverColumn: null,
        user_data: null,
        wipDialog: false,
        // 0 = no limit. Persisted to localStorage.
        wipLimits: { 'Todo': 0, 'In Progress': 0, 'On Hold': 0, 'Cancelled': 0, 'Done': 0 },
        // The board columns. `key` matches the backend status values.
        columns: [
            { key: 'Todo', label: 'Todo' },
            { key: 'In Progress', label: 'In Progress' },
            { key: 'Done', label: 'Done' },
            { key: 'On Hold', label: 'On Hold' },
            { key: 'Cancelled', label: 'Cancelled' },
        ],
        // Tickets grouped by status, keyed exactly like `columns[].key`.
        board: {
            'Todo': [],
            'In Progress': [],
            'Done': [],
            'On Hold': [],
            'Cancelled': [],
        },
      }
    },
    computed: {
        // Whether the "All Sprints" aggregate view is selected.
        isAllSprints() {
            return this.selectedSprint === 'ALL';
        },
        // Sprint dropdown items, with an "All Sprints" aggregate entry on top
        // whenever the project has at least one sprint.
        sprintOptions() {
            if (!this.sprints.length) return [];
            return [{ id: 'ALL', name: 'All Sprints' }, ...this.sprints];
        },
        boardTitle() {
            if (this.isAllSprints) return 'All Sprints Board';
            const sprint = this.sprints.find(s => s.id === this.selectedSprint);
            return sprint ? `${sprint.name} Board` : 'INT Board Ticket';
        },
        // The currently selected sprint object, used to constrain ticket due dates.
        selectedSprintObj() {
            return this.sprints.find(s => s.id === this.selectedSprint) || null;
        },
    },
    methods: {
        // authHeaders, formattedDate, initials, priorityColor and priorityIcon
        // come from the shared apiMixin / formatMixin so every screen renders
        // them identically.
        columnColor(key) {
            switch (key) {
                case 'Todo': return '#9aa0aa';
                case 'In Progress': return '#2c7be5';
                case 'On Hold': return '#f5a623';
                case 'Cancelled': return '#e5484d';
                case 'Done': return '#2e9e5b';
                default: return '#9aa0aa';
            }
        },
        // Left-edge accent stripe on each card, mirroring the priority color map.
        priorityAccent(priority) {
            switch (String(priority || '').toLowerCase()) {
                case 'critical':
                case 'urgent':
                case 'high': return '#e5484d';
                case 'medium': return '#f5a623';
                case 'low': return '#2c7be5';
                case 'lowest': return '#9aa0aa';
                default: return '#d3d5db';
            }
        },
        // A ticket is overdue if it has a past due date and is not in a
        // terminal column (Done / Cancelled).
        isTicketOverdue(ticket) {
            if (!ticket || !ticket.due_date) return false;
            if (ticket.status === 'Done' || ticket.status === 'Cancelled') return false;
            const parts = String(ticket.due_date).slice(0, 10).split('-');
            if (parts.length !== 3) return false;
            const due = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
            if (isNaN(due)) return false;
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            return due < today;
        },
        // Assignee objects are provided by the model's assignees_details append.
        assigneeList(ticket) {
            return Array.isArray(ticket.assignees_details) ? ticket.assignees_details : [];
        },
        // Deterministic avatar background so the same person is always one color.
        avatarColor(name) {
            const palette = ['#2c7be5', '#2e9e5b', '#f5a623', '#8b5cf6', '#e5484d', '#0ea5e9', '#d6409f'];
            const s = String(name || '?');
            let h = 0;
            for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
            return palette[h % palette.length];
        },
        filteredTickets(statusKey) {
            const tickets = this.board[statusKey] || [];
            if (!this.search) return tickets;
            const q = this.search.toLowerCase();
            return tickets.filter(t =>
                (t.name || '').toLowerCase().includes(q) ||
                (t.ticket_id || '').toLowerCase().includes(q)
            );
        },
        EventMessage(value) {
            this.isshow = true;
            this.message = value;
            setTimeout(() => { this.isshow = false; }, 1500);
            this.getBoard();
        },
        isOverLimit(key) {
            const limit = this.wipLimits[key];
            return limit > 0 && (this.board[key] || []).length > limit;
        },
        loadWipLimits() {
            try {
                const saved = JSON.parse(localStorage.getItem('wip_limits'));
                if (saved) this.wipLimits = { ...this.wipLimits, ...saved };
            } catch (e) { /* ignore malformed */ }
        },
        saveWipLimits() {
            // Coerce to non-negative integers.
            Object.keys(this.wipLimits).forEach(k => {
                this.wipLimits[k] = Math.max(0, parseInt(this.wipLimits[k]) || 0);
            });
            localStorage.setItem('wip_limits', JSON.stringify(this.wipLimits));
            this.wipDialog = false;
            this.EventMessage('WIP limits saved');
        },
        onDragStart(ticket, fromKey) {
            this.draggedTicket = ticket;
            this.draggedFrom = fromKey;
        },
        onDrop(toKey) {
            this.dragOverColumn = null;
            const ticket = this.draggedTicket;
            if (!ticket || this.draggedFrom === toKey) {
                this.draggedTicket = null;
                return;
            }
            // Optimistically move the card between columns.
            this.board[this.draggedFrom] = this.board[this.draggedFrom].filter(t => t.id !== ticket.id);
            ticket.status = toKey;
            this.board[toKey] = [ticket, ...this.board[toKey]];

            const updatedBy = this.user_data
                ? (this.user_data.company_id || this.user_data.email)
                : 'system';

            axios.post(utils._api(this.constant.update_ticket_status), {
                ticket_id: ticket.ticket_id,
                status: toKey,
                updated_by: updatedBy,
            }, this.authHeaders())
                .then((response) => {
                    // Surface a WIP warning if the destination is now over limit,
                    // otherwise the normal success message.
                    if (this.isOverLimit(toKey)) {
                        this.EventMessage(`Heads up: "${toKey}" is over its WIP limit`);
                    } else {
                        this.EventMessage(response.data.message);
                    }
                })
                .catch((error) => {
                    console.error('Error updating status:', error);
                    // Roll back on failure.
                    this.getBoard();
                    this.EventMessage('Failed to update status');
                });

            this.draggedTicket = null;
            this.draggedFrom = null;
        },
        resetBoard() {
            this.board = { 'Todo': [], 'In Progress': [], 'Done': [], 'On Hold': [], 'Cancelled': [] };
        },
        getProjects() {
            axios.get(utils._api(this.constant.list_projects), this.authHeaders())
                .then((response) => {
                    this.projects = response.data.data || [];
                    if (this.projects.length && !this.selectedProject) {
                        this.selectedProject = this.projects[0].id;
                        this.onProjectChange();
                    }
                })
                .catch(error => console.error("Error fetching projects:", error));
        },
        onProjectChange() {
            this.selectedSprint = null;
            this.sprints = [];
            this.resetBoard();
            if (!this.selectedProject) return;
            axios.post(utils._api(this.constant.list_sprints),
                { project_id: this.selectedProject }, this.authHeaders())
                .then((response) => {
                    this.sprints = response.data.data || [];
                    // Default to the active sprint if there is one.
                    const active = this.sprints.find(s => s.status === 'active');
                    this.selectedSprint = active ? active.id : (this.sprints[0]?.id ?? null);
                    if (this.selectedSprint) this.getBoard();
                })
                .catch(error => console.error("Error fetching sprints:", error));
        },
        getBoard() {
            this.resetBoard();
            if (!this.selectedSprint) return;
            // "All Sprints" aggregates every sprint's tickets in the project;
            // a specific sprint uses the per-sprint board endpoint.
            const url = this.isAllSprints ? this.constant.sprint_board_project : this.constant.sprint_board;
            const payload = this.isAllSprints
                ? { project_id: this.selectedProject }
                : { sprint_id: this.selectedSprint };
            axios.post(utils._api(url), payload, this.authHeaders())
                .then((response) => {
                    // Backend returns tickets already grouped by status.
                    const data = response.data.data || {};
                    this.board = {
                        'Todo': data['Todo'] || [],
                        'In Progress': data['In Progress'] || [],
                        'Done': data['Done'] || [],
                        'On Hold': data['On Hold'] || [],
                        'Cancelled': data['Cancelled'] || [],
                    };
                })
                .catch(error => console.error("Error fetching board:", error));
        },
    },
    mounted() {
        const userData = localStorage.getItem('user_data');
        if (userData) {
            try { this.user_data = JSON.parse(userData); } catch (e) { this.user_data = null; }
        }
        this.loadWipLimits();
        this.getProjects();
    }
  }
  </script>

  <style scoped>
  .int-legend {
      display: flex;
      flex-wrap: wrap;
      gap: 14px;
      margin-top: 4px;
      color: var(--int-text-muted);
      font-size: 0.78rem;
  }
  .int-legend span {
      display: inline-flex;
      align-items: center;
      gap: 2px;
  }

  /* --- Board: horizontal-scroll lane of fixed-width columns --- */
  .int-board {
      display: flex;
      gap: 14px;
      overflow-x: auto;
      padding: 4px 2px 14px;
      align-items: flex-start;
  }
  .int-column {
      flex: 0 0 300px;
      max-width: 300px;
      display: flex;
      flex-direction: column;
      background: var(--int-surface-muted);
      border: 1px solid var(--int-border);
      border-radius: var(--int-radius-md);
      max-height: calc(100vh - 300px);
      transition: background 0.15s ease, box-shadow 0.15s ease;
  }
  .int-column.drop-target {
      background: var(--int-yellow-soft);
      box-shadow: inset 0 0 0 2px var(--int-yellow);
  }
  .int-column-head {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 12px 14px;
      border-top: 3px solid var(--col-accent);
      border-radius: var(--int-radius-md) var(--int-radius-md) 0 0;
      position: sticky;
      top: 0;
      z-index: 1;
      background: var(--int-surface-muted);
  }
  .int-column-title {
      font-weight: 700;
      font-size: 0.85rem;
      color: var(--int-text-strong);
  }
  .int-dot {
      width: 9px;
      height: 9px;
      border-radius: 50%;
      display: inline-block;
      margin-right: 8px;
  }
  .int-column-body {
      display: flex;
      flex-direction: column;
      gap: 10px;
      padding: 10px;
      overflow-y: auto;
      min-height: 90px;
  }
  .int-empty {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 6px;
      padding: 22px 8px;
      border: 1.5px dashed var(--int-border);
      border-radius: var(--int-radius-sm);
      color: var(--int-text-faint);
      font-size: var(--int-fs-xs);
  }

  /* --- Ticket card --- */
  .int-ticket {
      position: relative;
      background: var(--int-surface);
      border: 1px solid var(--int-border);
      border-left: 3px solid var(--prio-accent);
      border-radius: var(--int-radius-sm);
      padding: 10px 12px;
      cursor: grab;
      box-shadow: var(--int-shadow-sm);
      transition: box-shadow 0.15s ease, transform 0.15s ease;
  }
  .int-ticket:hover {
      box-shadow: var(--int-shadow-md);
      transform: translateY(-1px);
  }
  .int-ticket:active {
      cursor: grabbing;
  }
  .int-ticket-top {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 6px;
  }
  .int-ticket-title {
      font-weight: 600;
      font-size: var(--int-fs-sm);
      color: var(--int-text-strong);
      line-height: 1.3;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
  }
  .int-ticket-meta {
      display: flex;
      flex-wrap: wrap;
      gap: 10px;
      margin-top: 6px;
      color: var(--int-text-muted);
      font-size: var(--int-fs-xs);
  }
  .int-ticket-meta .v-icon {
      vertical-align: -2px;
      margin-right: 2px;
  }
  .int-ticket-foot {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 8px;
      margin-top: 10px;
      padding-top: 8px;
      border-top: 1px solid var(--int-border);
  }
  .int-due {
      display: inline-flex;
      align-items: center;
      gap: 2px;
      font-size: var(--int-fs-xs);
      font-weight: 600;
      color: var(--int-text-muted);
  }
  .int-due.overdue {
      color: #e5484d;
  }
  .int-assignees {
      display: flex;
      align-items: center;
  }
  .int-avatar {
      color: #fff;
      font-size: 0.66rem;
      font-weight: 700;
      border: 2px solid var(--int-surface);
  }
  .int-more {
      margin-left: 4px;
      font-size: var(--int-fs-xs);
      font-weight: 600;
      color: var(--int-text-muted);
  }
  </style>
